import { useCallback, useMemo, useRef } from "react";
import { appInsightsEvents, trackEvent, useAppStore } from "../../common";

/** Scope tag attached to App Insights exceptions raised by the WebChat component. */
export type WebChatErrorScope = "tokenExchange" | "directLineConnection" | "init" | "tokenRefresh";

/**
 * Hook that returns a typed telemetry surface for the WebChat component.
 *
 * Wraps App Insights `trackException` + `trackEvent` so callers don't have to
 * pull `appInsights` / `context` from the store at every site. All event names
 * route through `appInsightsEvents.Usage.Actions.WebChat.*` for consistency
 * with the rest of the codebase.
 *
 * **Conversation correlation:** the active Direct Line `conversationId` is
 * tracked internally via `setConversationId(id)` and auto-injected into the
 * `customProperties` of every event and exception. Calling `setConversationId`
 * does NOT cause callbacks to re-create — the id lives in a ref, so callback
 * identity stays stable. This is how we join client-side events to the same
 * conversation in App Insights (and, when PVA→AI is enabled, to the
 * bot-side analytics).
 *
 * **Stability:** The returned object identity is stable across renders as long
 * as `appInsights` and `context` from the store are stable. This is critical —
 * callers wire the returned hook value into `useCallback` / `useEffect` deps,
 * and an unstable identity will infinite-loop those effects.
 */
export const useWebChatTelemetry = () => {
  // Use individual selectors instead of a tuple selector — a tuple selector
  // returns a fresh array on every render and would defeat the stability of
  // every downstream useCallback / useMemo that depends on `appInsights` or
  // `context`.
  const appInsights = useAppStore((state) => state.appInsights);
  const context = useAppStore((state) => state.context);

  // Conversation id is held in a ref so updating it doesn't invalidate any
  // memoized callbacks below.
  const conversationIdRef = useRef<string | undefined>(undefined);

  // Stable setter — never changes identity, safe to include in effect deps.
  const setConversationId = useCallback((id: string | undefined) => {
    conversationIdRef.current = id;
  }, []);

  // Build the extra custom-properties bag attached to every event. Reads from
  // the ref so the latest value is always picked up at call time.
  const getCustomProperties = useCallback(
    (extra?: Record<string, unknown>) => ({
      ...(conversationIdRef.current ? { conversationId: conversationIdRef.current } : {}),
      ...(extra ?? {}),
    }),
    [],
  );

  const error = useCallback(
    (scope: WebChatErrorScope, err: unknown) => {
      const exception = err instanceof Error ? err : new Error(String(err));
      appInsights.trackException(
        { exception },
        { component: "WebChat", scope, ...getCustomProperties() },
      );
    },
    [appInsights, getCustomProperties],
  );

  const opened = useCallback(
    () =>
      trackEvent(
        appInsights,
        appInsightsEvents.Usage.Actions.WebChat.Opened,
        context,
        getCustomProperties(),
      ),
    [appInsights, context, getCustomProperties],
  );
  const closed = useCallback(
    () =>
      trackEvent(
        appInsights,
        appInsightsEvents.Usage.Actions.WebChat.Closed,
        context,
        getCustomProperties(),
      ),
    [appInsights, context, getCustomProperties],
  );
  const messageSent = useCallback(
    (properties: { messageLength: number }) =>
      trackEvent(
        appInsights,
        appInsightsEvents.Usage.Actions.WebChat.MessageSent,
        context,
        getCustomProperties(properties),
      ),
    [appInsights, context, getCustomProperties],
  );
  const newChat = useCallback(
    () =>
      trackEvent(
        appInsights,
        appInsightsEvents.Usage.Actions.WebChat.NewChat,
        context,
        getCustomProperties(),
      ),
    [appInsights, context, getCustomProperties],
  );
  const retry = useCallback(
    () =>
      trackEvent(
        appInsights,
        appInsightsEvents.Usage.Actions.WebChat.Retry,
        context,
        getCustomProperties(),
      ),
    [appInsights, context, getCustomProperties],
  );

  const micPermissionRequested = useCallback(
    () =>
      trackEvent(
        appInsights,
        appInsightsEvents.Usage.Actions.WebChat.MicPermissionRequested,
        context,
        getCustomProperties(),
      ),
    [appInsights, context, getCustomProperties],
  );

  const micPermissionDenied = useCallback(
    () =>
      trackEvent(
        appInsights,
        appInsightsEvents.Usage.Actions.WebChat.MicPermissionDenied,
        context,
        getCustomProperties(),
      ),
    [appInsights, context, getCustomProperties],
  );

  const recordingStarted = useCallback(
    () =>
      trackEvent(
        appInsights,
        appInsightsEvents.Usage.Actions.WebChat.RecordingStarted,
        context,
        getCustomProperties(),
      ),
    [appInsights, context, getCustomProperties],
  );

  const recordingStopped = useCallback(
    () =>
      trackEvent(
        appInsights,
        appInsightsEvents.Usage.Actions.WebChat.RecordingStopped,
        context,
        getCustomProperties(),
      ),
    [appInsights, context, getCustomProperties],
  );

  const transcriptionRequested = useCallback(
    () =>
      trackEvent(
        appInsights,
        appInsightsEvents.Usage.Actions.WebChat.TranscriptionRequested,
        context,
        getCustomProperties(),
      ),
    [appInsights, context, getCustomProperties],
  );

  const transcriptionSucceeded = useCallback(
    (properties: { characterCount: number }) =>
      trackEvent(
        appInsights,
        appInsightsEvents.Usage.Actions.WebChat.TranscriptionSucceeded,
        context,
        getCustomProperties(properties),
      ),
    [appInsights, context, getCustomProperties],
  );

  const transcriptionFailed = useCallback(
    (properties: { reason: string }) =>
      trackEvent(
        appInsights,
        appInsightsEvents.Usage.Actions.WebChat.TranscriptionFailed,
        context,
        getCustomProperties(properties),
      ),
    [appInsights, context, getCustomProperties],
  );

  // Conversation outcome events.
  // `resolved`  : bot dispatched an `endOfConversation` event activity.
  // `abandoned` : user closed the chat after sending at least one message
  //               but the bot never signalled end-of-conversation.
  // Together with `opened` / `closed` these enable a containment-rate metric
  // analogous to Copilot Studio's Resolved / Abandoned / Unengaged outcomes
  // (see learn.microsoft.com/.../analytics-improve-agent-effectiveness).
  const resolved = useCallback(
    () =>
      trackEvent(
        appInsights,
        appInsightsEvents.Usage.Actions.WebChat.Resolved,
        context,
        getCustomProperties(),
      ),
    [appInsights, context, getCustomProperties],
  );
  const abandoned = useCallback(
    () =>
      trackEvent(
        appInsights,
        appInsightsEvents.Usage.Actions.WebChat.Abandoned,
        context,
        getCustomProperties(),
      ),
    [appInsights, context, getCustomProperties],
  );

  // Latency metric — emitted via trackMetric (not trackEvent) so App
  // Insights' native percentile aggregations work without custom Kusto.
  // Currently used for time-to-first-bot-reply; expressed in milliseconds.
  const trackLatency = useCallback(
    (latencyMs: number) => {
      appInsights.trackMetric(
        { name: appInsightsEvents.Performance.WebChat.ResponseLatencyMs, average: latencyMs },
        getCustomProperties(),
      );
    },
    [appInsights, getCustomProperties],
  );

  // The returned object identity MUST be stable across renders. Callers wire
  // this into useCallback/useEffect deps and an unstable identity will
  // infinite-loop them.
  return useMemo(
    () => ({
      error,
      opened,
      closed,
      messageSent,
      newChat,
      retry,
      micPermissionRequested,
      micPermissionDenied,
      recordingStarted,
      recordingStopped,
      transcriptionRequested,
      transcriptionSucceeded,
      transcriptionFailed,
      resolved,
      abandoned,
      trackLatency,
      setConversationId,
    }),
    [
      error,
      opened,
      closed,
      messageSent,
      newChat,
      retry,
      micPermissionRequested,
      micPermissionDenied,
      recordingStarted,
      recordingStopped,
      transcriptionRequested,
      transcriptionSucceeded,
      transcriptionFailed,
      resolved,
      abandoned,
      trackLatency,
      setConversationId,
    ],
  );
};
